import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap/Modal'
import { toast } from 'react-toastify'
import { BeatLoader } from 'react-spinners'
import paymentService from '../../../services/paymentService'
import plansAndBundlesService from '../../../services/plansAndBundlesService'
import sessionService from '../../../services/sessionService'
import { ADMIN_ROLES } from '../../../constants'
import { loadStripe } from '@stripe/stripe-js'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import styles from './modal.module.scss'
import { useNavigate } from 'react-router-dom'
import { stripeKey } from '../../../environment'
import {
  canAddMonthlyBundles,
  getNextRenewalDate,
  isMonthlyBundle,
  isPaymentMethodRequired,
  isStripeSubscription,
} from '../../../utils/subscriptionUtils'

const options = {
  hideProgressBar: true,
}

const stripePromise = loadStripe(stripeKey)

const cardElementOptions = {
  style: {
    base: {
      iconColor: '#c4f0ff',
      color: '#fff',
      fontWeight: '500',
      fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#87BBFD',
      },
    },
    invalid: {
      iconColor: '#FFC7EE',
      color: '#FFC7EE',
    },
  },
}

/**
 * Card entered for this payment only
 */
const AddCardForm = ({ onPaymentMethod, submitting, payLabel, handleClose }) => {
  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || elements === null) {
      return null
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
    })
    if (error && error.message) {
      toast.error('Error adding card', options)
    } else if (paymentMethod) {
      onPaymentMethod(paymentMethod.id)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardElement options={cardElementOptions} />

      <Modal.Footer style={{ background: '#121621' }}>
        <button className='btn btn-lightblue' disabled={submitting} type='submit'>
          {payLabel}
        </button>

        <button className='btn btn-lightblue' type='button' onClick={handleClose}>
          Close
        </button>
      </Modal.Footer>
    </form>
  )
}

AddCardForm.propTypes = {
  onPaymentMethod: PropTypes.func,
  submitting: PropTypes.bool,
  payLabel: PropTypes.string,
  handleClose: PropTypes.func,
}

const AddMinutes = ({ handleClose, show, organizationId, subscription, onPurchased }) => {
  const [state, setState] = useState({
    minutes: [],
    minuteId: '',
    error: null,
  })
  // Add to the monthly plan is the default; "Only this period" is the one-time purchase
  const [recurring, setRecurring] = useState(true)
  const [useNewCard, setUseNewCard] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()

  const userRole = sessionService.getUserRole()
  const isMasterAdmin = userRole === ADMIN_ROLES.master

  useEffect(() => {
    const loadMinutes = () => {
      plansAndBundlesService
        .getBundles()
        .then((result) => {
          setState((prev) => ({ ...prev, minutes: result }))
        })
        .catch(() => {})
    }
    loadMinutes()
  }, [])

  const handleChange = (event) => {
    const { name, value } = event.target
    setState((prev) => ({
      ...prev,
      [name]: value,
      error: null,
    }))
  }

  const { minutes, minuteId, error } = state
  const selectedBundle = minutes.find((minute) => minute._id === minuteId)
  // The master admin grants bundles without a charge, so there is nothing to renew
  const monthlyAvailable =
    !isMasterAdmin && canAddMonthlyBundles(subscription) && isMonthlyBundle(selectedBundle)
  const addToPlan = monthlyAvailable && recurring
  // Admin-granted and legacy plans have no saved card, the card is always entered
  const savedCardAvailable = isStripeSubscription(subscription)
  const showCardForm = !isMasterAdmin && (!savedCardAvailable || useNewCard)
  const renewalDate = getNextRenewalDate(subscription)
  const trialing = subscription?.status === 'trialing'

  const purchase = (nonce) => {
    if (!minuteId) {
      setState((prev) => ({ ...prev, error: 'Pricing plan is required!' }))
      return
    }
    setSubmitting(true)
    paymentService
      .purchaseExtraMinutes(organizationId, nonce, minuteId, addToPlan)
      .then(() => {
        toast.success(
          addToPlan
            ? 'The bundle is purchased and added to your monthly plan!'
            : 'Extra minutes are successfully purchased!',
          options,
        )
        handleClose()
        if (onPurchased) onPurchased()
        else navigate('/dashboard/organizations')
      })
      .catch((err) => {
        // The error is shown by httpService; without a saved card the card form is opened
        if (isPaymentMethodRequired(err)) setUseNewCard(true)
      })
      .finally(() => setSubmitting(false))
  }

  const describeCharge = () => {
    if (!selectedBundle) return null
    if (isMasterAdmin) return 'The bundle is added to the current period without a charge.'
    const price = `$${selectedBundle.price}`
    if (addToPlan) {
      return trialing
        ? `${price} is charged now. Then ${price}/month together with your plan, from the first paid period${
            renewalDate ? ` (${renewalDate})` : ''
          }.`
        : `${price} is charged now. Then ${price}/month together with your plan${
            renewalDate ? `, next on ${renewalDate}` : ''
          }. You can remove it on the Plan page at any time; it then ends at the next renewal.`
    }
    return `${price} is charged now, for the current period only.`
  }

  const payLabel = selectedBundle ? `Pay $${selectedBundle.price}` : 'Pay'

  return (
    <div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header style={{ background: '#121621' }} closeVariant='white' closeButton>
          <Modal.Title>Purchase more voice minutes or texts (SMS/MMS)</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#121621' }}>
          <form className='form-wrapper'>
            <table className='table mb-0 tb-list-groups'>
              <thead className='lightblue-bg'>
                <tr>
                  <th className='border-0'> # </th>
                  <th className='border-0'>Price</th>
                  <th className='border-0'>Call credits </th>
                  <th className='border-0'>Texts (SMS/MMS)</th>
                  <th className='border-0 text-center'> </th>
                </tr>
              </thead>
              <tbody>
                {minutes.map((minute, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <span>$</span>
                      {minute.price}
                    </td>
                    <td>{minute.minutes}</td>
                    <td>{minute.sms}</td>
                    <td>
                      <div className='custom-radio-container'>
                        <input
                          type='radio'
                          id={`sel-${minute._id}`}
                          className='custom-radio'
                          name='minuteId'
                          value={minute._id}
                          onChange={handleChange}
                        />
                        <label htmlFor={`sel-${minute._id}`}></label>
                        <div className='check'>
                          <div className='inside'></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {error && <div className={'alert alert-danger'}>{error}</div>}
          </form>

          {selectedBundle && (
            <div className={styles.bundleOptions}>
              {monthlyAvailable ? (
                <>
                  <div className={styles.bundleOption}>
                    <input
                      type='radio'
                      id='bundle-recurring'
                      name='bundleRecurring'
                      checked={recurring}
                      onChange={() => setRecurring(true)}
                    />
                    <label htmlFor='bundle-recurring'>
                      Add to my monthly plan
                      <span>Renewed with your plan every month until you remove it</span>
                    </label>
                  </div>
                  <div className={styles.bundleOption}>
                    <input
                      type='radio'
                      id='bundle-once'
                      name='bundleRecurring'
                      checked={!recurring}
                      onChange={() => setRecurring(false)}
                    />
                    <label htmlFor='bundle-once'>
                      Only this period
                      <span>A one-time purchase for the current period</span>
                    </label>
                  </div>
                </>
              ) : (
                <p className='small light-grey mb-2'>
                  {isMasterAdmin
                    ? 'Bundles added by the master admin are for the current period only.'
                    : isMonthlyBundle(selectedBundle)
                    ? 'Bundles can be added to the monthly plan only with a monthly plan and automatic billing on. This purchase is for the current period only.'
                    : 'This bundle is available for the current period only.'}
                </p>
              )}
              <p className={styles.bundleCharge}>{describeCharge()}</p>
              <p className='small light-grey mb-0'>
                Unused bundle minutes and texts carry over to the next period once.
              </p>
            </div>
          )}

          {submitting ? (
            <div className=' m-5 text-center'>
              <BeatLoader size={15} color={'#299bff'} loading={submitting} />
            </div>
          ) : null}
          {minuteId && showCardForm && (
            <div className={styles.cardElement}>
              <Elements stripe={stripePromise}>
                <AddCardForm
                  onPaymentMethod={purchase}
                  submitting={submitting}
                  payLabel={payLabel}
                  handleClose={handleClose}
                />
              </Elements>
              {savedCardAvailable && (
                <button
                  type='button'
                  className={`btn btn-link ${styles.cardSwitch}`}
                  onClick={() => setUseNewCard(false)}
                >
                  Use my saved card
                </button>
              )}
            </div>
          )}
        </Modal.Body>
        {!showCardForm && (
          <Modal.Footer style={{ background: '#121621' }}>
            {minuteId && !isMasterAdmin && (
              <button
                type='button'
                className={`btn btn-link ${styles.cardSwitch}`}
                onClick={() => setUseNewCard(true)}
              >
                Use a different card
              </button>
            )}
            {minuteId ? (
              <button
                className='btn btn-lightblue'
                disabled={submitting}
                onClick={() => purchase(null)}
              >
                {isMasterAdmin ? 'Save' : `${payLabel} with saved card`}
              </button>
            ) : null}
            <button className='btn btn-lightblue' onClick={handleClose}>
              Close
            </button>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  )
}

AddMinutes.propTypes = {
  handleClose: PropTypes.func,
  show: PropTypes.bool,
  organizationId: PropTypes.string,
  subscription: PropTypes.object,
  onPurchased: PropTypes.func,
}

export default AddMinutes
